import React, { useState, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';
import get from 'lodash/get';

import TabsLayout, {
  Tabs,
  Tab,
  TabsContent,
} from 'legacy-src/utility/TabsLayout';
import Message from 'legacy-src/utility/Message';
import { getEditQuestion, saveQuestionMutation } from 'legacy-src/queries';
import { AlertBox, LoadingSpinner, TabContent } from 'legacy/packages/ft-cb';
import useNavigation from 'chameleon/ui-stack/hooks/useNavigation';
import { QuestionTypes } from 'legacy/packages/unit-outline/src/constants';
import LearnosityProvider from 'legacy/src/utility/LearnosityProvider';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import Icon from 'chameleon/ui-stack/ui/base/icon';

import {
  PROVIDER_MODES,
  switchMode,
} from 'legacy/src/components/learnosity/utils/helpers';
import useTags from './useTags';
import useValidation from './useValidation';
import usePublish from './usePublish';
import usePublishEffects from './usePublishEffects';
import { ScoringProvider, useScoring } from './EditScoring/ScoringProvider';
import { tabs, validationActionTypes } from '../constants';
import EditorTitle from '../EditorTitle';
import EditorHeader from '../EditorHeader';
import McqEditor from '../McqEditor';
import FrqEditor from '../FrqEditor';
import { addQuestion } from 'legacy/src/components/assessment/state/actions';
import { AP_COMPUTER_SCIENCE_PRINCIPLES } from 'chameleon-src/constants/subjects';
import './EditItem.scss';
import { IsQuestionEditable } from 'legacy/src/components/questionBank/helpers';
import { Link } from 'react-router';
import A11y from 'chameleon/ui-stack/utilities/a11y';
import { findQBSubject } from 'legacy-src/components/learnosity/utils/helpers';
import { QUESTION_TYPES } from '../AuthorItem';

const EditItem = ({
  router,
  saveQuestion,
  userData,
  initOptions,
  isLoading,
  question,
  refetchInitOptions,
  type,
  reference,
  addQuestion,
  isOwner,
  featureFlags,
  // FY-31483: Check if this page is used for a new question
  isAuthoringNewPage,
  authoringPageActions,
}) => {
  const {
    editCbProvidedQuestion,
    editExistingQuestionFrq,
    editExistingQuestion,
    copyQuestionAssessmentFlag,
    twoLevelQBFilterFF,
  } = featureFlags;

  const { params, queryParams, goTo } = useNavigation({
    router,
  });

  const { itemId } = queryParams;
  const qbPath = `/${queryParams.masterSubject}/question_bank`;

  const isFrq =
    (isAuthoringNewPage
      ? params.qtype === QUESTION_TYPES.FRQ
      : queryParams.type === QUESTION_TYPES.FRQ) ||
    type === QuestionTypes.FREE_RESPONSE_QUESTION;
  const isEditingItem = Boolean(itemId);

  // Based on where this component is used we get `subjectInfo` in different ways
  // 1. Used as a standalone route, the subjectInfo params are coming directly from the url query
  // 2. Used as component, they're searched in the current user adminSubjects, based on the router param
  const subjectInfo = useMemo(() => {
    if (isAuthoringNewPage) {
      return findQBSubject(userData.adminSubjects, get(params, 'subject'));
    }
    // fake subject info object so we don't need to refactor all children
    return {
      initId: queryParams.subjectInfoInitId,
      name: queryParams.subjectInfoName,
      masterSubjectId: queryParams.masterSubject,
    };
  }, []);

  const editMcq = !isFrq && editExistingQuestion;
  const editFrq = isFrq && editExistingQuestionFrq;
  const showEdit = IsQuestionEditable(
    isOwner,
    editMcq,
    editFrq,
    editCbProvidedQuestion,
  );

  const {
    tags,
    questionTags,
    selectedTagsInfo,
    allRequiredAreSelected,
    isTagSelected,
    setTags,
    updateTags,
  } = useTags(question);

  const [selectedTab, setSelectedTab] = useState(tabs.edit);
  const [title, setTitle] = useState('');
  const [authoringApiInstance, setAuthoringApiInstance] = useState(false);
  const [passageApiInstance, setPassageApiInstance] = useState(null);
  const [masterApiInstance, setMasterApiInstance] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [disableTabs, setDisableTabs] = useState(true);
  const [savedQuestion, setQuestionSaved] = useState(null);
  const [isDisabledTitle, setIsDisabledTitle] = useState(false);

  const titleTextbox = useRef(null);
  const [scoreState] = useScoring();

  const [
    validations,
    dispatchValidation,
    mcqValidationErrors,
    frqValidationErrors,
  ] = useValidation();

  const [publish, publishQuestion] = usePublish({ masterApiInstance });

  const itemIdIfNotDuplicating = {};

  if (isOwner) {
    itemIdIfNotDuplicating.itemId = itemId;
  }

  const copyQuestion = Boolean(queryParams.copyQuestion);
  const replaceQuestion = Boolean(queryParams.replaceQuestion);
  const showCopyQuestion =
    copyQuestion &&
    copyQuestionAssessmentFlag &&
    IsQuestionEditable(isOwner, editMcq, editFrq, editCbProvidedQuestion);
  let replaceQuestionParams = {};

  if (copyQuestion) {
    const { assign } = Object;

    replaceQuestionParams = assign(replaceQuestionParams, {
      assessment_id: queryParams.quizId,
      item_id: itemId,
      keep_old_question: true,
    });
  }

  if (replaceQuestion) {
    const { assign } = Object;

    replaceQuestionParams = assign(replaceQuestionParams, {
      assessment_id: queryParams.quizId,
      item_id: itemId,
      keep_old_question: false,
    });
  }

  const questionSaved = (question) => {
    setQuestionSaved(question);
    addQuestion(question);
  };

  const handleTitleFocus = () =>
    titleTextbox && titleTextbox.current && titleTextbox.current.focus();

  const [previouslyCreatedPassage] = usePublishEffects({
    router,
    query: queryParams,
    question,
    questionTags,
    authoringApiInstance,
    passageApiInstance,
    masterApiInstance,
    setTags,
    allRequiredAreSelected,
    isTagSelected,
    setTitle,
    publish,
    validations,
    dispatchValidation,
    setSelectedTab,
    isEditing: isEditingItem,
    defaultTab: tabs.edit,
    appendCopyToTitle: copyQuestion || replaceQuestion,
    copyQuestion,
    replaceQuestion,
    savedQuestion,
    isFrq,
    isDisabledTitle,
    setIsDisabledTitle,
    handleTitleFocus,
    isAuthoringNewPage,
    authoringPageActions,
  });
  const errorsOnQuestionValidation = isFrq
    ? frqValidationErrors()
    : mcqValidationErrors();
  const scoringSectionErrors = isFrq ? validations.scoringSectionErrors : 0;

  const textMCQ = {
    title: 'Author Multiple Choice Question',
    helpOverall: `Provide a question name, a correct answer, and keywords on what the question
    assesses in order to publish your question.`,
  };

  const isCSPSubject =
    parseInt(params.subject, 10) === AP_COMPUTER_SCIENCE_PRINCIPLES;

  const textFRQ = {
    title: `Author ${
      isCSPSubject ? 'Performance Task' : 'Free Response Question'
    }`,
    helpOverall: `Provide a question name, a response type, scoring section and required keyword
    tags in order to publish your question.`,
  };

  const text = isFrq ? textFRQ : textMCQ;

  const isLearnosityInEditMode = selectedTab === tabs.edit;

  async function handlePublish() {
    await publishQuestion({
      setShowValidation,
      authoringApiInstance,
      passageApiInstance,
      errorsOnQuestionValidation,
      scoringSectionErrors,
      validations,
      subjectInfo,
      initId: userData.me.initId,
      selectedTagsInfo,
      tags,
      saveQuestion,
      title,
      itemIdIfNotDuplicating,
      isFrq,
      navigateToAfterPublishingPath: queryParams.returnPath || qbPath,
      scoreState,
      clearValidations: () => {
        dispatchValidation({ type: validationActionTypes.clear });
      },
      addQuestion: questionSaved,
      copyQuestion,
      replaceQuestion,
      replaceQuestionParams,
    });
  }

  const onCancelClick = () =>
    isAuthoringNewPage ? authoringPageActions.reload() : goTo(returnPath);

  const flattenHierarchy = useRef({
    tree: {},
    parentByChild: {},
  });

  if (isLoading || !subjectInfo) {
    return (
      <TabContent data-test-authoring-ui>
        <div className="page_wrapper">
          <LoadingSpinner />
        </div>
      </TabContent>
    );
  }

  if ((!showEdit && itemId) || (copyQuestion && !showCopyQuestion)) {
    const errorMessage = 'Permission denied';
    return (
      <TabContent data-test-authoring-ui>
        <div className="page_wrapper">
          <AlertBox color="red">{errorMessage}</AlertBox>
        </div>
      </TabContent>
    );
  }

  const cbQuestionCopyReplaceMessage = [
    <strong>You are editing a copy of an AP question</strong>,
    <br />,
    `The version you publish will be added to the Question Bank and your assessment,
    without changing the original AP question.
    Give it a unique name to make it easy to find later.`,
  ];

  const returnPath = queryParams.returnPath || qbPath;

  return (
    <div className="bg-white">
      {!isAuthoringNewPage && (
        <div className="h-20 border-b border-gray-300 flex px-8">
          <div className="flex-grow flex items-center">
            <A11y>
              <Link
                onlyActiveOnIndex=""
                to={qbPath}
                className="text-gray-900 mr-2"
              >
                Question Bank
              </Link>
            </A11y>
            <span className="mr-2">
              <Icon name="navNext" />
            </span>
            <span>{text.title}</span>
          </div>
          <ButtonWithIcon
            isIcon
            isNaked
            onClick={() => goTo(returnPath)}
            icon="xmark"
            className="self-center"
            iconClassName="text-black"
            accessibility={{ 'aria-label': 'Close' }}
          />
        </div>
      )}

      <div className="container mx-auto px-6 lg:px-20">
        <EditorHeader title={text.title} qbPath={returnPath} />

        <Message>
          {!isOwner && (copyQuestion || replaceQuestion)
            ? cbQuestionCopyReplaceMessage
            : text.helpOverall}
        </Message>

        {showValidation && !validations.hasName && (
          <Message className="--error">
            In order to publish a question, you must enter a question name.
          </Message>
        )}
        <EditorTitle
          value={title}
          onChange={(value) => {
            dispatchValidation({
              type: validationActionTypes.hasName,
              payload: value,
            });
            setTitle(value);
          }}
          isDisabled={isDisabledTitle}
          onSubmit={handlePublish}
          onCancelClick={onCancelClick}
          ref={titleTextbox}
          isAuthoringNewPage={isAuthoringNewPage}
        />
      </div>

      <TabContent data-test-authoring-ui>
        <TabsLayout>
          <Tabs
            className="border-b border-gray-500"
            classNameChildren="container mx-auto px-6 lg:px-20"
          >
            <Tab
              isActive={selectedTab === tabs.edit}
              onClick={() => {
                if (selectedTab !== tabs.edit) {
                  switchMode('question-editor-container', PROVIDER_MODES.edit);
                  setSelectedTab(tabs.edit);
                }
              }}
            >
              Edit Question
              {errorsOnQuestionValidation > 0 && showValidation && (
                <strong className="error-mark">
                  Errors <strong>{errorsOnQuestionValidation}</strong>
                </strong>
              )}
            </Tab>

            {isFrq && (
              <Tab
                disabled={disableTabs}
                isActive={selectedTab === tabs.scoring}
                onClick={() => {
                  if (selectedTab !== tabs.scoring) {
                    setSelectedTab(tabs.scoring);
                  }
                }}
              >
                Scoring Section
                {validations.scoringSectionErrors > 0 && showValidation && (
                  <strong className="error-mark">
                    Errors <strong>{validations.scoringSectionErrors}</strong>
                  </strong>
                )}
              </Tab>
            )}

            <Tab
              disabled={disableTabs}
              isActive={selectedTab === tabs.preview}
              text="Preview Question"
              onClick={() => {
                if (selectedTab !== tabs.preview) {
                  switchMode(
                    'question-editor-container',
                    PROVIDER_MODES.preview,
                  );
                  setSelectedTab(tabs.preview);
                }
              }}
            />
          </Tabs>

          <TabsContent className="container mx-auto px-6 lg:px-20">
            <div id="question-editor-container">
              {!isFrq ? (
                <McqEditor
                  showValidation={showValidation}
                  createdPassage={previouslyCreatedPassage}
                  isEdit={isLearnosityInEditMode}
                  isLoading={isLoading}
                  subjectInfo={subjectInfo}
                  itemId={itemId}
                  setPassageApiInstance={setPassageApiInstance}
                  onAuthoringApiInstanceReady={(app) => {
                    setDisableTabs(false);
                    setAuthoringApiInstance(app);
                  }}
                  authoringApiInstance={authoringApiInstance}
                  initOptions={initOptions}
                  dispatchValidation={dispatchValidation}
                  validations={validations}
                  tags={tags}
                  updateTags={updateTags}
                  flattenHierarchy={flattenHierarchy}
                  twoLevelQBFilterFF={twoLevelQBFilterFF}
                />
              ) : (
                <FrqEditor
                  showValidation={showValidation}
                  isEdit={isLearnosityInEditMode}
                  isEditingItem={isEditingItem}
                  subjectInfo={subjectInfo}
                  itemId={itemId}
                  setPassageApiInstance={setPassageApiInstance}
                  onFinallyReady={() => setDisableTabs(false)}
                  onAuthoringApiInstanceReady={(app) => {
                    setAuthoringApiInstance(app);
                  }}
                  authoringApiInstance={authoringApiInstance}
                  initOptions={initOptions}
                  validations={validations}
                  tags={tags}
                  refetchInitOptions={refetchInitOptions}
                  updateTags={updateTags}
                  selectedTab={selectedTab}
                  learnosityItemReference={reference}
                  dispatchValidation={dispatchValidation}
                  flattenHierarchy={flattenHierarchy}
                  twoLevelQBFilterFF={twoLevelQBFilterFF}
                />
              )}
            </div>

            <div className="learnosity-question-editor-master-item-wrapper">
              {!isLoading && initOptions && (
                <LearnosityProvider
                  className="learnosity-question-editor-master-item"
                  containerElement="learnosity-question-editor-master-item"
                  apiType="authoring"
                  initOptions={initOptions}
                  readyListener={(app) => {
                    setMasterApiInstance(app);
                    !isEditingItem && setDisableTabs(false);
                    authoringPageActions && authoringPageActions.enableTabs();
                  }}
                  shouldDestroy={false}
                />
              )}
            </div>
          </TabsContent>
        </TabsLayout>
      </TabContent>
    </div>
  );
};

const withSaveQuestion = graphql(saveQuestionMutation, {
  name: 'saveQuestion',
});

const withEditQuestion = graphql(getEditQuestion, {
  options: ({ userData, location: { query }, params }) => {
    // Try first query params
    let subjectId;
    if (query.subjectInfoInitId) {
      subjectId = parseInt(query.subjectInfoInitId, 10);
    } else {
      // Otherwise we're getting it from the current selected subject
      subjectId = get(params, 'subject');
      subjectId = findQBSubject(userData.adminSubjects, subjectId).initId;
    }

    return {
      variables: {
        subjectId,
        itemId: parseInt(query.itemId, 10),
        copyQuestion:
          Boolean(query.copyQuestion) || Boolean(query.replaceQuestion),
      },
      fetchPolicy: 'network-only',
    };
  },
  props: ({ data: { error, editQuestion, loading, refetch } }) => {
    let props = { isLoading: loading };
    if (error) {
      props = { ...props, error: error.message };
    } else if (editQuestion) {
      const { learnositySignedRequest, isOwner, question } = editQuestion;

      const initOptions = JSON.parse(learnositySignedRequest);

      const { reference } = initOptions.request;

      props = {
        ...props,
        initOptions,
        isLoading: loading,
        refetchInitOptions: refetch,
        reference,
        type: question && question.__typename,
        isOwner,
        question,
      };
    }
    return props;
  },
});

const withScoringProvider = (Component) => (props) => (
  <ScoringProvider>
    <Component {...props} />
  </ScoringProvider>
);

const withErrorHandler = (Component) => (props) => {
  const { error } = props;
  if (error) {
    return (
      <TabContent>
        <div className="page_wrapper">
          <AlertBox color="red" icon="circle_warn">
            {error}
          </AlertBox>
        </div>
      </TabContent>
    );
  }
  return <Component {...props} />;
};

const mapStateToProps = (state) => {
  const { featureFlag, userData } = state;
  return {
    userData,
    featureFlags: {
      editCbProvidedQuestion: featureFlag.edit_cb_provided_question,
      editExistingQuestionFrq: featureFlag.edit_existing_question_frq,
      editExistingQuestion: featureFlag.edit_existing_question,
      copyQuestionAssessmentFlag: featureFlag.copy_question_assessment,
      twoLevelQBFilterFF: featureFlag.two_level_qb_filter,
    },
  };
};

export default compose(
  withEditQuestion,
  withSaveQuestion,
  withScoringProvider,
  connect(mapStateToProps, { addQuestion }),
  withErrorHandler,
)(EditItem);
